import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import {
  Brain,
  Box,
  Layers,
  Wrench,
  Eye,
  FolderOpen,
  Settings,
  LogOut,
  Menu,
  X,
  Zap,
  Shield,
} from 'lucide-react';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navSections = [
    {
      label: 'Tools',
      items: [
        { name: 'Full Workflow', icon: Layers, path: '/dashboard/smart-support' },
        { name: 'Generate Supports', icon: Zap, path: '/dashboard/generate-supports' },
        { name: 'Repair & Analysis', icon: Wrench, path: '/dashboard/repair-analysis' },
      ],
    },
    {
      label: 'Diagnosis',
      items: [
        { name: 'Vision Diagnosis', icon: Eye, path: '/dashboard/diagnosis' },
      ],
    },
    {
      label: null, // No label — standalone items
      items: [
        { name: 'My Projects', icon: FolderOpen, path: '/dashboard/projects' },
        { name: 'Settings', icon: Settings, path: '/dashboard/settings' },
      ],
    },
  ];

  if (user?.role === 'admin') {
    navSections.push({
      label: 'Admin',
      items: [
        { name: 'Admin Panel', icon: Shield, path: '/dashboard/admin' },
      ],
    });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-bg-dark flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-bg-light border-r border-text-muted/20
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-text-muted/20">
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <div className="relative">
                <Brain className="w-8 h-8 text-primary" />
                <Box className="w-4 h-4 text-accent absolute -bottom-0.5 -right-0.5" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                PrintMind
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-text-muted hover:text-text-primary"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 overflow-y-auto" data-testid="sidebar-nav">
            {navSections.map((section, sIdx) => (
              <div key={sIdx} className={sIdx > 0 ? 'mt-5' : ''}>
                {section.label && (
                  <p className="px-4 mb-2 text-[11px] font-semibold uppercase tracking-widest text-text-muted">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.name}
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                        className={`
                          w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition text-sm
                          ${active
                            ? 'bg-primary text-white font-medium'
                            : 'text-text-secondary hover:bg-bg-dark hover:text-text-primary'
                          }
                        `}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <Icon className="w-[18px] h-[18px]" />
                        <span>{item.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-text-muted/20">
            <div className="mb-3 px-4 py-2.5 rounded-lg" style={{
              background: (user?.subscription_plan === 'pro') ? 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(72,230,177,0.1))' :
                         (user?.subscription_plan === 'studio') ? 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(249,115,22,0.1))' :
                         'var(--background-dark)'
            }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-text-muted uppercase tracking-wide">Plan</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  user?.subscription_plan === 'pro' ? 'bg-primary/20 text-primary' :
                  user?.subscription_plan === 'studio' ? 'bg-yellow-500/20 text-yellow-500' :
                  'bg-text-muted/20 text-text-muted'
                }`} data-testid="plan-badge">
                  {(user?.subscription_plan || 'free').toUpperCase()}
                </span>
              </div>
              <p className="text-sm font-medium text-text-primary truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-2.5 text-text-secondary hover:text-error hover:bg-error/10 rounded-lg transition text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-[18px] h-[18px]" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-14 bg-bg-light border-b border-text-muted/20 flex items-center justify-between px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-text-muted hover:text-text-primary"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 lg:flex-none" />
          <div className="flex items-center space-x-3">
            {user?.subscription_plan && user.subscription_plan !== 'free' && (
              <span className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                user.subscription_plan === 'pro'
                  ? 'bg-primary/15 text-primary border border-primary/25'
                  : 'bg-yellow-500/15 text-yellow-500 border border-yellow-500/25'
              }`} data-testid="header-plan-badge">
                <Zap className="w-3 h-3" />
                {user.subscription_plan.toUpperCase()}
              </span>
            )}
            <ThemeToggle />
            <div className="hidden sm:block">
              <span className="text-sm text-text-secondary">Welcome,</span>
              <span className="ml-1.5 text-sm font-semibold text-text-primary">
                {user?.full_name || user?.email?.split('@')[0]}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
